import { useEffect, useMemo, useState } from "react";
import { useSlaSettings } from "@/hooks/useSlaSettings";
import { evaluateTicketSlaBreach } from "@/lib/slaBreach";
import { sortTicketsForInbox } from "@/lib/inboxSort";

function openedByLabel(code) {
  if (code === "bot") return "Bot";
  if (code === "user") return "Customer";
  return "Agent";
}

function ticketStatusBadgeClass(status) {
  if (status === "pending") return "bg-amber-100 text-amber-900 ring-amber-200/80";
  if (status === "on_hold") return "bg-sky-100 text-sky-900 ring-sky-200/80";
  if (status === "resolved") return "bg-slate-200/90 text-slate-700 ring-slate-300/80";
  return "bg-emerald-50 text-emerald-900 ring-emerald-200/70";
}

function classifyTicketGroup(ticket) {
  if (ticket.group === "refund") return "refund";
  if (ticket.group === "technical") return "technical";
  const haystack = `${ticket.subject ?? ""} ${ticket.conversationSummary ?? ""}`.toLowerCase();
  const refundKeywords = ["refund", "chargeback", "billing", "invoice", "payment", "subscription", "renewal"];
  return refundKeywords.some((k) => haystack.includes(k)) ? "refund" : "technical";
}

export default function TicketingConversationList({
  selectedTicketId,
  onSelectTicket,
  tickets = [],
  ticketQueueSections = [],
  ticketsLoading,
  conversations = [],
  listError,
  onRetryList,
  panelTitle = "Tickets",
  panelSubtitle = "Ticketing queue",
  expandStatusSectionId = null,
}) {
  const { settings, now } = useSlaSettings();
  const [slaBreachOnly, setSlaBreachOnly] = useState(false);
  const [queueView, setQueueView] = useState("status");
  const [collapsedSections, setCollapsedSections] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({ refund: true, technical: true });

  const ticketSlaById = useMemo(() => {
    const m = new Map();
    for (const t of tickets) m.set(t.id, evaluateTicketSlaBreach(t, settings, now));
    return m;
  }, [tickets, settings, now]);

  const ticketBreaches = useMemo(() => tickets.filter((t) => ticketSlaById.get(t.id)?.breached).length, [tickets, ticketSlaById]);

  useEffect(() => {
    if (ticketQueueSections.length === 0) return;
    setCollapsedSections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const section of ticketQueueSections) {
        if (!(section.id in next)) {
          next[section.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [ticketQueueSections]);

  useEffect(() => {
    if (!expandStatusSectionId || ticketQueueSections.length === 0) return;
    const ids = new Set(ticketQueueSections.map((s) => s.id));
    if (!ids.has(expandStatusSectionId)) return;
    setCollapsedSections((prev) => ({ ...prev, [expandStatusSectionId]: false }));
  }, [expandStatusSectionId, ticketQueueSections]);

  const groupedTickets = useMemo(() => {
    const next = { refund: [], technical: [] };
    for (const ticket of tickets) {
      const key = classifyTicketGroup(ticket);
      if (!slaBreachOnly || ticketSlaById.get(ticket.id)?.breached) next[key].push(ticket);
    }
    next.refund = sortTicketsForInbox(next.refund, settings, now);
    next.technical = sortTicketsForInbox(next.technical, settings, now);
    return next;
  }, [tickets, slaBreachOnly, ticketSlaById, settings, now]);

  const renderTicketRow = (ticket, sectionLabel) => {
    const linked = conversations.find((c) => c.id === ticket.conversationId);
    const active = selectedTicketId === ticket.id;
    const sla = ticketSlaById.get(ticket.id);
    const breached = Boolean(sla?.breached);
    const rowClass = breached
      ? active
        ? "border-l-[3px] border-l-rose-600 bg-rose-50 ring-2 ring-inset ring-rose-200/90 dark:bg-rose-950/40 dark:ring-rose-800/60"
        : "border-l-[3px] border-l-rose-600 bg-rose-50 hover:bg-rose-100/80 dark:bg-rose-950/30 dark:hover:bg-rose-950/50"
      : active
        ? "border-l-[3px] border-l-teal-600 bg-teal-50 dark:bg-teal-950/40"
        : "border-l-[3px] border-l-transparent hover:bg-white/80 dark:hover:bg-slate-800/50";

    return (
      <button
        key={ticket.id}
        type="button"
        onClick={() => onSelectTicket(ticket)}
        title={breached ? sla?.reasons?.join(" · ") : undefined}
        className={`flex w-full flex-col gap-1 border-b border-slate-100/80 px-4 py-3.5 text-left transition-all last:border-b-0 dark:border-slate-700/50 ${rowClass}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className={`font-mono text-sm font-bold ${breached ? "text-rose-800 dark:text-rose-400" : "text-teal-700 dark:text-teal-400"}`}>#{ticket.number ?? ticket.id}</span>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
            {breached && (
              <span className="rounded-md bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                SLA
              </span>
            )}
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${ticketStatusBadgeClass(ticket.status)}`}>
              {sectionLabel}
            </span>
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-700/80 dark:text-slate-300">
              {openedByLabel(ticket.openedBy)}
            </span>
          </div>
        </div>
        <p className="line-clamp-2 text-xs font-medium leading-snug text-slate-800 dark:text-slate-200">{ticket.subject}</p>
        <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500 dark:text-slate-400">
          {linked && <span className="truncate">{linked.user.name}</span>}
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span>{new Date(ticket.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
        </div>
        {breached && <p className="text-[11px] font-semibold text-rose-800 dark:text-rose-400">Needs agent response</p>}
      </button>
    );
  };

  return (
    <div className="ticketing-list-root">
      <div className="ticketing-list-main">
        <div className="border-b border-slate-200/80 bg-white px-4 py-2.5 dark:border-slate-700/60 dark:bg-slate-900">
          <h2 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">{panelTitle}</h2>
          <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{panelSubtitle} · Tickets</p>
        </div>

        <div className="border-b border-slate-200/80 bg-white px-4 py-2.5 dark:border-slate-700/60 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-slate-800 dark:text-slate-200">
                {tickets.length} ticket{tickets.length === 1 ? "" : "s"}
              </span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span className="font-semibold text-rose-700 dark:text-rose-400">{ticketBreaches} SLA</span>
            </div>
            <button
              type="button"
              onClick={() => setSlaBreachOnly((v) => !v)}
              aria-pressed={slaBreachOnly}
              className={`rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 transition-colors ${
                slaBreachOnly ? "bg-rose-600 text-white ring-rose-700" : "bg-white text-rose-700 ring-rose-200 hover:bg-rose-50 dark:bg-slate-800 dark:text-rose-400 dark:ring-rose-800/60 dark:hover:bg-rose-950/40"
              }`}
            >
              SLA breach
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setQueueView("status")}
              aria-pressed={queueView === "status"}
              className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                queueView === "status" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              Status
            </button>
            <button
              type="button"
              onClick={() => setQueueView("groups")}
              aria-pressed={queueView === "groups"}
              className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                queueView === "groups" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              Groups
            </button>
          </div>
        </div>

        <div className="ticketing-list-scroll">
          {listError && (
            <div className="mx-3 mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/90 px-3 py-2 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300">
              <span>{listError.message}</span>
              {onRetryList && (
                <button type="button" onClick={onRetryList} className="font-semibold underline">
                  Retry
                </button>
              )}
            </div>
          )}

          {ticketsLoading && (
            <div className="space-y-4 px-3 py-4">
              {[1, 2, 3].map((block) => (
                <div key={block} className="space-y-2">
                  <div className="h-3 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
                </div>
              ))}
            </div>
          )}

          {!ticketsLoading && tickets.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">No tickets yet. Create one from a chat or automation.</p>
          )}

          {!ticketsLoading && tickets.length > 0 && slaBreachOnly && ticketBreaches === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">No SLA breaches in the ticket queue.</p>
          )}

          {!ticketsLoading &&
            tickets.length > 0 &&
            !(slaBreachOnly && ticketBreaches === 0) &&
            (queueView === "status"
              ? ticketQueueSections.map((section) => {
                  const inStatus = tickets.filter((t) => t.status === section.id);
                  let inSection = sortTicketsForInbox(inStatus, settings, now);
                  if (slaBreachOnly) inSection = inSection.filter((t) => ticketSlaById.get(t.id)?.breached);
                  const isCollapsed = Boolean(collapsedSections[section.id]);

                  return (
                    <div key={section.id} className="border-b border-slate-100/90 last:border-b-0 dark:border-slate-700/50">
                      <button
                        type="button"
                        onClick={() => setCollapsedSections((prev) => ({ ...prev, [section.id]: !prev[section.id] }))}
                        className="ticketing-section-toggle"
                        aria-expanded={!isCollapsed}
                      >
                        <div className="flex items-center gap-2">
                          <i
                            className={`ri-arrow-right-s-line text-sm text-slate-500 transition-transform ${isCollapsed ? "rotate-0" : "rotate-90"}`}
                            aria-hidden
                          />
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{section.label}</span>
                        </div>
                        <span className="text-[10px] font-semibold tabular-nums text-slate-400">{inSection.length}</span>
                      </button>

                      {!isCollapsed &&
                        (inSection.length === 0 ? (
                          <p className="px-4 py-3 text-center text-[11px] font-medium text-slate-400 dark:text-slate-500">No tickets in this state.</p>
                        ) : (
                          inSection.map((ticket) => renderTicketRow(ticket, section.label))
                        ))}
                    </div>
                  );
                })
              : [
                  { id: "refund", label: "Refund", items: groupedTickets.refund },
                  { id: "technical", label: "Technical", items: groupedTickets.technical },
                ].map((group) => {
                  const isCollapsed = Boolean(collapsedGroups[group.id]);
                  return (
                    <div key={group.id} className="border-b border-slate-100/90 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                        className="ticketing-section-toggle"
                        aria-expanded={!isCollapsed}
                      >
                        <div className="flex items-center gap-2">
                          <i
                            className={`ri-arrow-right-s-line text-sm text-slate-500 transition-transform ${isCollapsed ? "rotate-0" : "rotate-90"}`}
                            aria-hidden
                          />
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{group.label}</span>
                        </div>
                        <span className="text-[10px] font-semibold tabular-nums text-slate-400">{group.items.length}</span>
                      </button>

                      {!isCollapsed &&
                        (group.items.length === 0 ? (
                          <p className="px-4 py-3 text-center text-[11px] font-medium text-slate-400">No tickets in this group.</p>
                        ) : (
                          group.items.map((ticket) =>
                            renderTicketRow(ticket, ticketQueueSections.find((s) => s.id === ticket.status)?.label ?? ticket.status.replace("_", " ")),
                          )
                        ))}
                    </div>
                  );
                }))}
        </div>
      </div>
    </div>
  );
}
